import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/primitives';
import { fetchSeoKeywords } from '@/data/sources';
import {
  fetchConnections,
  fetchTargetIndex,
  generateFromKeyword,
  generateFromPrompt,
  syncThenRetryGenerate,
  type GenerateOutcome,
} from '@/data/content-generate';
import {
  buildTargetPageOptions,
  suggestedTargetUrl,
  type TargetPageOption,
} from '@/data/content-generate-model';
import { ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/utils';

/* ----------------------------------------------------------------------------
 * GAP #1 — direct keyword-targeted content generation, Copilot surfaces.
 *
 * TWO INPUT MODES, both feeding the SAME server-side generate path (the shared
 * ContentEngine::checkDuplicate runs first, so a duplicate is refused 409 and
 * shown as a clear "already exists / published on <site>" notice — never a
 * generic error):
 *
 *   MODE (i)  the Copilot offers the TRACKED-KEYWORD LIST as a pick-one
 *             dropdown ("Which tracked keyword?"). Selecting one issues the
 *             SAME generate call the Keywords/Rankings row-action does. NO
 *             free-text parse — the user picks from the existing tracked list.
 *   MODE (ii) a structured-prompt box ("create a blog post targeting <url> for
 *             <location>"). The raw prompt is forwarded verbatim; the BE does
 *             all extraction + target-page validation. Renders the result, the
 *             honest validation message, or the 409 duplicate notice.
 *
 * STALENESS FAIL-CLOSED (title-decoupling spec 2026-08-04): when the site's
 * published-content index has never been synced, the BE refuses with 422
 * `target_inventory_not_synced` carrying a `sync_target_id`. We do NOT show a
 * raw error — we render an actionable "Sync site content now" action that calls
 * the EXISTING connector index-sync route and, on success, re-fires the SAME
 * generate ONCE. A STALE-but-synced 201 renders a non-blocking "as of {date}"
 * note in the success box.
 *
 * This lives in the SEO/content FE area (NOT voice). Draft-only by construction:
 * nothing is published or sent from here.
 * -------------------------------------------------------------------------- */

type ResultTone = 'ok' | 'dup' | 'err' | 'sync' | 'fail';
interface PanelResult {
  tone: ResultTone;
  title: string;
  body: string;
}
type Busy = 'keyword' | 'prompt' | null;

export function ContentGeneratePanel({
  activeProjectId,
}: {
  activeProjectId: number | null;
}) {
  const [selectedKeywordId, setSelectedKeywordId] = useState('');
  // Option (b): the owner-chosen internal-link target page ('' = server default).
  const [selectedTargetUrl, setSelectedTargetUrl] = useState('');
  // The MANUALLY-chosen connected site (connector target id) whose page index the
  // target-page picker shows. A domain can now serve MORE THAN ONE connected site
  // (e.g. booksesh.com root + booksesh.com/partners share one SEO project), so the
  // old single auto-resolve was ambiguous — the user picks explicitly here.
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [result, setResult] = useState<PanelResult | null>(null);
  // The fail-closed sync moment: non-null while a `target_inventory_not_synced`
  // 422 is showing. `targetId === null` = no connected site (no Sync button).
  const [syncMoment, setSyncMoment] = useState<{ targetId: number | null } | null>(null);
  // The LAST generate request, captured so "Sync now" can re-fire it verbatim.
  const lastGenerate = useRef<null | (() => Promise<GenerateOutcome>)>(null);

  // MODE (i): the tracked-keyword list for the active domain — the SAME source
  // the Keywords page renders. Only enabled once a domain is being viewed.
  const keywordsQ = useQuery({
    queryKey: ['seo', 'keywords', activeProjectId],
    queryFn: () => fetchSeoKeywords(String(activeProjectId)),
    enabled: activeProjectId != null,
  });
  const keywords = keywordsQ.data ?? [];

  // --- Option (b): the stacked target-page picker -------------------------
  // List ALL of the workspace's connected sites. The user picks ONE explicitly
  // (the "Site / domain" selector); its id is the connector target id. No
  // connection -> the selectors show an empty state (never crash / never toast).
  const connectionsQ = useQuery({
    queryKey: ['connector', 'connections', activeProjectId],
    queryFn: () => fetchConnections(),
    enabled: activeProjectId != null,
  });

  // The DEFAULT connected site: the one assigned to the active SEO domain, else
  // the first ACTIVE connection. This is only the default seed — the user may
  // override it with the "Site / domain" selector, and a manual pick persists.
  const defaultTargetId =
    activeProjectId == null
      ? null
      : (connectionsQ.data?.find((c) => c.project_id === activeProjectId)?.id ??
        connectionsQ.data?.find((c) => (c.status ?? 'active').toLowerCase() === 'active')?.id ??
        null);

  // Seed / re-seed the selected site to the default whenever the resolved default
  // changes (connections load, the active domain changes, or the previously
  // defaulted site leaves the set). Keying on the resolved value — exactly like
  // the suggestedUrl effect below — means a stable set + stable domain runs this
  // once, so a MANUAL pick within that set PERSISTS (it never re-fires).
  useEffect(() => {
    setSelectedTargetId(defaultTargetId);
  }, [defaultTargetId]);

  // The selected keyword's text drives ?topic= so the BE can suggest a match.
  const selectedKeywordText = keywords.find((k) => k.id === selectedKeywordId)?.keyword ?? '';

  // The chosen site's synced content index (pages/posts/cpts). Gated so we only
  // fetch once a domain is chosen AND a connected site is selected.
  const indexQ = useQuery({
    queryKey: ['connector', 'target-index', selectedTargetId, selectedKeywordText],
    queryFn: () => fetchTargetIndex(selectedTargetId as number, selectedKeywordText),
    enabled: activeProjectId != null && selectedTargetId != null,
  });
  const targetOptions = buildTargetPageOptions(indexQ.data);
  const targetGroups = targetOptions.reduce<Record<string, TargetPageOption[]>>((acc, o) => {
    (acc[o.group] ??= []).push(o);
    return acc;
  }, {});
  const suggestedUrl = suggestedTargetUrl(indexQ.data);

  // Pre-select the server's suggested target whenever a NEW suggestion arrives
  // (a new keyword -> new ?topic= -> fresh suggested_target; or a NEW site). A
  // manual page pick persists until the suggestion changes; switching site resets
  // it (the previously-picked page belongs to a different site).
  useEffect(() => {
    setSelectedTargetUrl(suggestedUrl);
  }, [suggestedUrl, selectedTargetId]);

  // fromRetry = the outcome came from the post-sync auto-retry, so a second
  // `inventory_not_synced` must NOT re-offer Sync (honest dead-end instead).
  const showOutcome = (o: GenerateOutcome, fromRetry = false) => {
    if (o.kind === 'ready') {
      setSyncMoment(null);
      const staleNote =
        o.inventoryStale && o.inventoryAsOf
          ? ` Angle varied against your site’s published content as of ${formatRelative(
              o.inventoryAsOf,
            )} — sync the index to refresh.`
          : '';
      setResult({
        tone: 'ok',
        title: 'Blog draft ready',
        body:
          (o.title ? `“${o.title}” — ` : '') +
          'Find it in the Blog / Articles library. Draft only — nothing published or sent.' +
          staleNote,
      });
    } else if (o.kind === 'duplicate') {
      setSyncMoment(null);
      setResult({ tone: 'dup', title: 'That content already exists', body: o.message });
    } else if (o.kind === 'inventory_not_synced') {
      if (fromRetry) {
        // The sync ran but found zero published posts — honest dead-end, no loop.
        setSyncMoment(null);
        setResult({
          tone: 'err',
          title: 'No published content to vary against',
          body: 'The site was synced, but it has no published posts yet for the new article to vary against. Nothing was generated — publish some content first, then try again.',
        });
      } else {
        setSyncMoment({ targetId: o.syncTargetId });
        setResult({ tone: 'sync', title: 'Sync the site’s content first', body: o.message });
      }
    } else if (o.kind === 'failed') {
      // A real generation failure — render it in the CLEARLY-VISIBLE danger tone
      // so it is never mistaken for "nothing happened". The mapped message tells
      // the user what to do next (pick a different keyword vs. try again).
      setSyncMoment(null);
      setResult({ tone: 'fail', title: 'Couldn’t generate a draft', body: o.message });
    } else {
      // 'invalid' — an honest validation message (bad prompt/target/keyword);
      // behaviour unchanged.
      setSyncMoment(null);
      setResult({ tone: 'err', title: 'Couldn’t generate that', body: o.message });
    }
  };

  const showError = (e: unknown) => {
    setSyncMoment(null);
    setResult({
      tone: 'fail',
      title: 'Couldn’t generate a draft',
      body:
        e instanceof ApiError && e.message
          ? `${e.message} Nothing was created — try again.`
          : 'The content service returned an unexpected response. Nothing was created — try again.',
    });
  };

  // "Sync site content now" -> sync the connector index, then re-fire the SAME
  // generate ONCE. Sync-route errors (503/409/404) become honest inline states.
  const syncMut = useMutation({
    mutationFn: () => {
      const tid = syncMoment?.targetId ?? null;
      const retry = lastGenerate.current;
      if (tid == null || !retry) throw new Error('no_sync_target');
      return syncThenRetryGenerate(tid, retry);
    },
    onSuccess: ({ outcome }) => showOutcome(outcome, true),
    onError: (e) => {
      setSyncMoment(null);
      if (e instanceof ApiError && (e.status === 503 || e.code === 'connector_disabled')) {
        setResult({
          tone: 'err',
          title: 'Site sync unavailable',
          body: 'The site connection isn’t active — check it under Connections, then try again. Nothing was generated.',
        });
      } else if (e instanceof ApiError && e.status === 409) {
        setResult({
          tone: 'err',
          title: 'Site sync isn’t available right now',
          body: `${e.message || 'The site’s content index can’t be synced at the moment.'} Try again shortly — nothing was generated.`,
        });
      } else if (e instanceof ApiError && e.status === 404) {
        setResult({
          tone: 'err',
          title: 'Site sync unavailable',
          body: 'That site connection couldn’t be found — check it under Connections. Nothing was generated.',
        });
      } else {
        setResult({
          tone: 'err',
          title: 'Site sync could not be completed',
          body:
            e instanceof ApiError && e.message
              ? `${e.message} Nothing was generated — try again.`
              : 'The sync service returned an unexpected response. Nothing was generated — try again.',
        });
      }
    },
  });

  const runKeyword = async () => {
    const kw = keywords.find((k) => k.id === selectedKeywordId);
    if (!kw || activeProjectId == null || busy) return;
    setResult(null);
    setSyncMoment(null);
    setBusy('keyword');
    const gen = () =>
      generateFromKeyword(
        {
          id: kw.id,
          keyword: kw.keyword,
          location_code: kw.location_code,
          language: kw.language,
        },
        selectedTargetUrl,
      );
    lastGenerate.current = gen;
    try {
      showOutcome(await gen());
    } catch (e) {
      showError(e);
    } finally {
      setBusy(null);
    }
  };

  const runPrompt = async () => {
    if (prompt.trim() === '' || activeProjectId == null || busy) return;
    setResult(null);
    setSyncMoment(null);
    setBusy('prompt');
    const gen = () => generateFromPrompt(prompt, activeProjectId);
    lastGenerate.current = gen;
    try {
      showOutcome(await gen());
    } catch (e) {
      showError(e);
    } finally {
      setBusy(null);
    }
  };

  const disabled = activeProjectId == null;

  const resultTone: Record<ResultTone, string> = {
    ok: 'border-success/30 bg-success/10 text-success',
    dup: 'border-ai/30 bg-ai-wash/40 text-text-primary',
    err: 'border-border-default bg-inset text-text-secondary',
    sync: 'border-ai/30 bg-ai-wash/40 text-text-primary',
    fail: 'border-danger/40 bg-danger/10 text-danger',
  };

  return (
    <div className="space-y-3 rounded-md border border-ai/25 bg-ai-wash/20 p-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
          ✧ generate content
        </span>
        <span className="rounded-sm border border-ai/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ai">
          Draft only
        </span>
      </div>

      {disabled ? (
        <p className="rounded-md border border-dashed border-border-default p-2 text-[11px] text-text-tertiary">
          Choose a domain (the “Viewing:” chip above) to generate a blog draft for a tracked keyword.
        </p>
      ) : (
        <>
          {/* MODE (i): pick-one dropdown from the tracked-keyword list. */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold text-text-secondary" htmlFor="cg-keyword">
              Which tracked keyword?
            </label>
            <div className="space-y-2">
              <div className="relative">
                <select
                  id="cg-keyword"
                  aria-label="Tracked keyword"
                  value={selectedKeywordId}
                  disabled={keywordsQ.isPending || keywords.length === 0}
                  onChange={(e) => setSelectedKeywordId(e.target.value)}
                  className="h-control w-full min-w-0 appearance-none truncate rounded-lg border border-border-default bg-canvas pl-3 pr-9 text-sm text-text-primary outline-none transition-colors focus:border-ai focus:ring-2 focus:ring-ai/30 disabled:opacity-60"
                >
                  <option value="">
                    {keywordsQ.isPending
                      ? 'Loading keywords…'
                      : keywords.length === 0
                        ? 'No tracked keywords yet'
                        : 'Select a keyword…'}
                  </option>
                  {keywords.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.keyword}
                    </option>
                  ))}
                </select>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </div>
              <Button
                variant="ai"
                className="w-full"
                disabled={selectedKeywordId === '' || busy !== null || syncMut.isPending}
                onClick={() => void runKeyword()}
              >
                {busy === 'keyword' ? (
                  'Generating…'
                ) : (
                  <>
                    <span aria-hidden="true">✧</span>Generate
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Site / domain selector — explicitly pick WHICH connected site's pages
              the target-page picker below should list. A single SEO domain can now
              serve more than one connected site (root + /partners share a project),
              so this removes the old single-target ambiguity. Never crashes: one
              site still renders; zero sites disables with a hint. */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold text-text-secondary" htmlFor="cg-site">
              Site / domain
            </label>
            {(() => {
              const conns = connectionsQ.data ?? [];
              const loading = connectionsQ.isPending;
              const none = !loading && conns.length === 0;
              return (
                <select
                  id="cg-site"
                  aria-label="Connected site to generate content for"
                  value={selectedTargetId == null ? '' : String(selectedTargetId)}
                  disabled={none || loading}
                  onChange={(e) => {
                    // Switching site invalidates the previously-picked page (it
                    // belongs to a different site) — reset to the server default.
                    setSelectedTargetId(e.target.value === '' ? null : Number(e.target.value));
                    setSelectedTargetUrl('');
                  }}
                  className="h-control min-w-0 w-full rounded-md border border-border-default bg-canvas px-2 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30 disabled:opacity-60"
                >
                  {loading ? (
                    <option value="">Loading connected sites…</option>
                  ) : none ? (
                    <option value="">No connected site yet — connect one first</option>
                  ) : (
                    conns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.site_label || c.site_url || `Site ${c.id}`}
                      </option>
                    ))
                  )}
                </select>
              );
            })()}
            <p className="text-[10px] text-text-tertiary">
              Pick which connected site’s pages the “Target page” list below should show.
            </p>
          </div>

          {/* Option (b): the STACKED target-page picker — its OWN control below
              the keyword dropdown (NOT merged into it). Drives MODE (i)'s
              internal-link target. Empty/loading states never crash or toast. */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold text-text-secondary" htmlFor="cg-target-page">
              Target page (optional)
            </label>
            {(() => {
              const hasTarget = selectedTargetId != null;
              const loading = connectionsQ.isPending || (hasTarget && indexQ.isPending);
              const empty = !loading && (!hasTarget || targetOptions.length === 0);
              return (
                <select
                  id="cg-target-page"
                  aria-label="Target page for the internal link"
                  value={selectedTargetUrl}
                  disabled={empty || loading}
                  onChange={(e) => setSelectedTargetUrl(e.target.value)}
                  className="h-control min-w-0 w-full rounded-md border border-border-default bg-canvas px-2 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30 disabled:opacity-60"
                >
                  {loading ? (
                    <option value="">Loading pages…</option>
                  ) : empty ? (
                    <option value="">
                      {hasTarget
                        ? 'No synced pages yet — sync the site’s content first'
                        : 'No connected site yet — connect one to pick a page'}
                    </option>
                  ) : (
                    <>
                      <option value="">Use the best-matched page (default)</option>
                      {Object.entries(targetGroups).map(([group, opts]) => (
                        <optgroup key={group} label={group}>
                          {opts.map((o) => (
                            <option key={o.url} value={o.url}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </>
                  )}
                </select>
              );
            })()}
            <p className="text-[10px] text-text-tertiary">
              Leave on “best-matched page” and I’ll pick the strongest internal-link target for you.
            </p>
          </div>

          {/* MODE (ii): structured prompt — forwarded verbatim (BE extracts). */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold text-text-secondary" htmlFor="cg-prompt">
              …or describe it
            </label>
            <textarea
              id="cg-prompt"
              aria-label="Describe the blog post to generate"
              rows={2}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. create a blog post targeting https://yoursite/services/web-design for london"
              className="w-full resize-y rounded-md border border-border-default bg-canvas px-2 py-1.5 text-sm text-text-primary outline-none placeholder:text-text-tertiary focus:border-ai focus:ring-2 focus:ring-ai/30"
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-text-tertiary">
                I read the prompt; you approve the draft. Nothing is published.
              </span>
              <Button
                variant="ghost"
                disabled={prompt.trim() === '' || busy !== null || syncMut.isPending}
                onClick={() => void runPrompt()}
              >
                {busy === 'prompt' ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </div>
        </>
      )}

      {result && (
        <div
          role="status"
          className={`space-y-2 rounded-md border px-2.5 py-2 text-[12px] ${resultTone[result.tone]}`}
        >
          <div>
            <div className="font-semibold">{result.title}</div>
            <p className="mt-0.5 leading-snug">{result.body}</p>
          </div>

          {/* Fail-closed sync-now affordance (only while a not-synced 422 shows). */}
          {syncMoment &&
            (syncMoment.targetId != null ? (
              <Button
                variant="primary"
                disabled={syncMut.isPending}
                onClick={() => syncMut.mutate()}
              >
                {syncMut.isPending ? 'Syncing…' : 'Sync site content now'}
              </Button>
            ) : (
              <p className="leading-snug text-text-tertiary">
                No connected site found for this project — connect it under Connections, then sync its
                content index.
              </p>
            ))}
        </div>
      )}
    </div>
  );
}
